/**
 * « Exporter » une animation en vidéo verticale (lot 7 : YouTube Shorts,
 * TikTok, Instagram Reels) — pipeline YouTube Studio (0107) comme les défis :
 *
 *   1. `animation-video` (edge fn) : la version PUBLIÉE, avec ta voix ou, sur
 *      les parties sans voix, une voix de synthèse (toujours signalée), et la
 *      bande-son choisie pour cette vidéo ; une ligne `youtube_videos` (0673) ;
 *      puis l'APERÇU, lancé par la même fonction (publications en `draft`) :
 *      le worker vidéo de ton ordinateur rend la vidéo image par image, la
 *      dépose (`youtube-renders`) et s'arrête — rien n'est publié ;
 *   2. la vidéo rendue se regarde dans le panneau, et son lien (valable
 *      7 jours) s'ouvre ou se copie, même si une publication a échoué ;
 *   3. « Publier » : `youtube-enqueue` en mode `publish`, sans nouveau rendu ;
 *      chaque plateforme réussit ou échoue seule (ex. YouTube à reconnecter).
 *
 * Réponses vérifiées par Zod (tables et edge fns hors des types générés).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import type { AnimationMix } from '@/lib/animations/audio';

const db = supabase as unknown as SupabaseClient;

export type AnimationVideoPlatform = 'youtube' | 'tiktok' | 'instagram_reels';

export const VIDEO_PLATFORMS: readonly {
  id: AnimationVideoPlatform;
  label: string;
  /** compte relié (`social_oauth_accounts.platform`) */
  account: 'youtube' | 'tiktok' | 'instagram';
}[] = [
  { id: 'youtube', label: 'YouTube Short', account: 'youtube' },
  { id: 'tiktok', label: 'TikTok', account: 'tiktok' },
  { id: 'instagram_reels', label: 'Instagram Reels', account: 'instagram' },
];

const platformsOf = (p: readonly AnimationVideoPlatform[]) => [...p];

const syntheticSchema = z.array(z.object({ key: z.string(), label: z.string() }));

/**
 * Son choisi pour UNE vidéo : `default` (son par défaut de la bibliothèque),
 * `none` (sans), `bundled` (jingle JeRaisonne livré avec l'app) ou l'id d'un
 * son de la bibliothèque (« Sons des animations »).
 */
export type VideoSoundChoice = 'default' | 'none' | 'bundled' | (string & {});

/** Phases de la méthode H.O.R.A (`scene.hora`). */
export const VIDEO_HORA_PHASES = ['H', 'O', 'R', 'A'] as const;
export type VideoHoraPhase = (typeof VIDEO_HORA_PHASES)[number];

export interface VideoSounds {
  music: VideoSoundChoice;
  /**
   * musique propre à une phase : absente ou `default` = celle de l'animation,
   * `none` = sans musique sur la phase, sinon l'id d'une musique de la bibliothèque
   */
  musicPhases: Partial<Record<VideoHoraPhase, VideoSoundChoice>>;
  jingleIntro: VideoSoundChoice;
  jingleOutro: VideoSoundChoice;
  /** volume de la musique pour cette vidéo (0 … 1) ; `null` : celui du son */
  musicGain: number | null;
  /** volume des jingles (0 … 1) ; `null` : celui du son */
  jingleIntroGain: number | null;
  jingleOutroGain: number | null;
  /** volume de la voix : × 1 = telle qu'enregistrée, jusqu'à × VOICE_GAIN_MAX */
  voiceGain: number;
}

/** Miroir de VOICE_GAIN_MAX (@jeraisonne/db/youtube). */
export const VOICE_GAIN_MAX = 3;

export const DEFAULT_VIDEO_SOUNDS: VideoSounds = {
  music: 'default',
  musicPhases: {},
  jingleIntro: 'default',
  jingleOutro: 'default',
  musicGain: null,
  jingleIntroGain: null,
  jingleOutroGain: null,
  voiceGain: 1,
};

/** Sons de départ d'un export : sons par défaut + volumes réglés (0677). PUR. */
export function videoSoundsFromMix(mix: AnimationMix | null | undefined): VideoSounds {
  if (!mix) return DEFAULT_VIDEO_SOUNDS;
  return {
    ...DEFAULT_VIDEO_SOUNDS,
    musicGain: mix.music_gain,
    jingleIntroGain: mix.jingle_intro_gain,
    jingleOutroGain: mix.jingle_outro_gain,
    voiceGain: Math.min(VOICE_GAIN_MAX, Math.max(0, mix.voice_gain)),
  };
}

/** Volumes d'un export → réglage commun (app + vidéos suivantes). PUR. */
export function mixFromVideoSounds(s: VideoSounds): AnimationMix {
  return {
    voice_gain: Math.round(Math.min(VOICE_GAIN_MAX, Math.max(0, s.voiceGain)) * 100) / 100,
    music_gain: s.musicGain,
    jingle_intro_gain: s.jingleIntroGain,
    jingle_outro_gain: s.jingleOutroGain,
  };
}

/** Ce que la vidéo joue (noms) ; `null` : sans. */
export const soundLabelsSchema = z.object({
  music: z.string().nullable(),
  jingleIntro: z.string().nullable(),
  jingleOutro: z.string().nullable(),
  /** phases avec leur propre musique (`null` : sans) */
  musicPhases: z.record(z.enum(VIDEO_HORA_PHASES), z.string().nullable()).optional(),
});
export type VideoSoundLabels = z.infer<typeof soundLabelsSchema>;

/** Noms des sons d'une bande-son enregistrée (`animation_payload.soundtrack`). */
export function soundLabelsOf(soundtrack: unknown): VideoSoundLabels | null {
  if (!soundtrack || typeof soundtrack !== 'object') return null;
  const st = soundtrack as Record<string, { label?: unknown; url?: unknown } | null | undefined>;
  const name = (s: { label?: unknown; url?: unknown } | null | undefined): string | null =>
    s ? (typeof s.label === 'string' && s.label ? s.label : 'Jingle JeRaisonne') : null;
  const phases = st.musicPhases as unknown as
    | Partial<Record<VideoHoraPhase, { label?: unknown; url?: unknown } | null>>
    | undefined;
  const musicPhases =
    phases && typeof phases === 'object'
      ? Object.fromEntries(
          VIDEO_HORA_PHASES.filter((p) => phases[p] !== undefined).map((p) => [
            p,
            phases[p] ? name(phases[p]) : null,
          ]),
        )
      : {};
  return {
    music: name(st.music),
    jingleIntro: name(st.jingleIntro),
    jingleOutro: name(st.jingleOutro),
    ...(Object.keys(musicPhases).length > 0 ? { musicPhases } : {}),
  };
}

/** Phases présentes dans l'animation (scènes non interactives), dans l'ordre H.O.R.A. PUR. */
export function videoPhasesOf(
  doc: { scenes: readonly { hora?: string | undefined; interactive?: boolean }[] } | null,
): VideoHoraPhase[] {
  if (!doc) return [...VIDEO_HORA_PHASES];
  const seen = new Set(doc.scenes.filter((s) => !s.interactive).map((s) => s.hora));
  return VIDEO_HORA_PHASES.filter((p) => seen.has(p));
}

export const preparedVideoSchema = z.object({
  ok: z.literal(true),
  queued: z.boolean().optional(),
  video_id: z.string().uuid(),
  version_no: z.number().int().min(1),
  duration_ms: z.number().int().positive(),
  too_long_for_shorts: z.boolean(),
  synthetic: syntheticSchema,
  sounds: soundLabelsSchema.optional(),
  title: z.string(),
  description: z.string(),
  caption: z.string(),
});
export type PreparedVideo = z.infer<typeof preparedVideoSchema>;

export const VIDEO_ERROR_MESSAGES: Record<string, string> = {
  not_published: 'Publie d’abord l’animation dans l’app : la vidéo montre la version publiée.',
  tts_failed: 'La voix de synthèse n’a pas pu être faite : réessaie dans un instant.',
  invalid_document: 'La version publiée n’est pas valide : republie l’animation.',
  misconfigured: 'Clé de la voix de synthèse absente du serveur.',
  forbidden: 'Réservé aux admins.',
  not_found: 'Animation introuvable.',
  ALREADY_QUEUED: 'Un rendu de cette vidéo est déjà en cours.',
  // l'app n'envoie que des demandes valides : refusée = fonction pas à jour
  invalid_input:
    'Demande refusée par le serveur : la fonction « animation-video » n’est sans doute pas à jour (supabase functions deploy animation-video).',
  outdated_function:
    'La fonction « animation-video » du serveur n’est pas à jour : redéploie-la (supabase functions deploy animation-video).',
  unknown_sound: 'Un des sons choisis n’existe plus : recharge la page et choisis-en un autre.',
};

export class AnimationVideoError extends Error {
  constructor(
    readonly code: string,
    fallback?: string,
  ) {
    super(VIDEO_ERROR_MESSAGES[code] ?? fallback ?? 'La vidéo n’a pas pu être préparée.');
  }
}

/** Code d'erreur d'une edge fn (corps JSON `{ error, message }`). */
async function edgeError(error: unknown): Promise<AnimationVideoError> {
  let code = 'unknown';
  let message: string | undefined;
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: unknown; message?: unknown };
      if (typeof body.error === 'string') code = body.error;
      if (typeof body.message === 'string') message = body.message;
    } catch {
      /* message générique */
    }
  }
  return new AnimationVideoError(code, message);
}

/** Choix envoyés au serveur : seulement ce qui change des sons par défaut. */
export function soundsBody(
  s: VideoSounds | undefined,
): Record<string, string | number | Record<string, string>> | null {
  if (!s) return null;
  const out: Record<string, string | number | Record<string, string>> = {};
  if (s.music !== 'default') out.music = s.music;
  const phases = Object.fromEntries(
    VIDEO_HORA_PHASES.flatMap((p) => {
      const c = s.musicPhases?.[p];
      return c && c !== 'default' ? [[p, c]] : [];
    }),
  );
  if (Object.keys(phases).length > 0) out.music_phases = phases;
  if (s.jingleIntro !== 'default') out.jingle_intro = s.jingleIntro;
  if (s.jingleOutro !== 'default') out.jingle_outro = s.jingleOutro;
  const unit = (g: number) => Math.round(Math.min(1, Math.max(0, g)) * 1000) / 1000;
  if (s.musicGain !== null && s.music !== 'none') out.music_gain = unit(s.musicGain);
  if (s.jingleIntroGain !== null && s.jingleIntro !== 'none')
    out.jingle_intro_gain = unit(s.jingleIntroGain);
  if (s.jingleOutroGain !== null && s.jingleOutro !== 'none')
    out.jingle_outro_gain = unit(s.jingleOutroGain);
  if (Number.isFinite(s.voiceGain) && Math.abs(s.voiceGain - 1) > 0.005)
    out.voice_gain = Math.round(Math.min(VOICE_GAIN_MAX, Math.max(0, s.voiceGain)) * 100) / 100;
  return Object.keys(out).length > 0 ? out : null;
}

/** Prépare la vidéo et lance son aperçu (rendu sans publication). */
export async function prepareAnimationVideo(input: {
  animationId: string;
  levelLabel?: string | null;
  subjectLabel?: string | null;
  sounds?: VideoSounds;
  platforms: readonly AnimationVideoPlatform[];
}): Promise<PreparedVideo> {
  const sounds = soundsBody(input.sounds);
  const { data, error } = await supabase.functions.invoke('animation-video', {
    body: {
      action: 'prepare',
      platforms: platformsOf(input.platforms),
      animation_id: input.animationId,
      ...(input.levelLabel ? { level_label: input.levelLabel.slice(0, 40) } : {}),
      ...(input.subjectLabel ? { subject_label: input.subjectLabel.slice(0, 60) } : {}),
      ...(sounds ? { sounds } : {}),
    },
  });
  if (error) throw await edgeError(error);
  const parsed = preparedVideoSchema.safeParse(data);
  if (!parsed.success) throw new AnimationVideoError('invalid_response');
  return parsed.data;
}

/** Refait le rendu (aperçu, rien n'est publié) d'une vidéo déjà préparée. */
export async function rerenderAnimationVideo(
  videoId: string,
  platforms: readonly AnimationVideoPlatform[],
): Promise<void> {
  const { error } = await supabase.functions.invoke('animation-video', {
    body: { action: 'rerender', video_id: videoId, platforms: platformsOf(platforms) },
  });
  if (error) throw await edgeError(error);
}

/** Publie la vidéo rendue sur les plateformes (`youtube-enqueue`, sans nouveau rendu). */
export async function publishAnimationVideo(
  videoId: string,
  platforms: readonly AnimationVideoPlatform[],
): Promise<void> {
  const { error } = await supabase.functions.invoke('youtube-enqueue', {
    body: {
      video_id: videoId,
      platforms: platformsOf(platforms),
      privacy: 'public',
      mode: 'publish',
    },
  });
  if (error) throw await edgeError(error);
}

export const animationVideoKeys = {
  latest: (animationId: string) => ['animations', 'video', 'latest', animationId] as const,
  status: (videoId: string) => ['animations', 'video', 'status', videoId] as const,
};

/** Préparer (voix, document) puis lancer l'aperçu sur les plateformes choisies. */
export function useCreateAnimationVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      animationId: string;
      levelLabel?: string | null;
      subjectLabel?: string | null;
      sounds?: VideoSounds;
      platforms: readonly AnimationVideoPlatform[];
    }): Promise<PreparedVideo> => {
      const prepared = await prepareAnimationVideo(input);
      // fonction pas encore redéployée : elle n'a pas lancé l'aperçu
      if (prepared.queued !== true) throw new AnimationVideoError('outdated_function');
      return prepared;
    },
    onSuccess: (_d, input) =>
      qc.invalidateQueries({ queryKey: animationVideoKeys.latest(input.animationId) }),
  });
}

/** Rendu raté (worker arrêté, erreur passagère) : même vidéo, nouveau rendu. */
export function useRetryAnimationVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { videoId: string; platforms: readonly AnimationVideoPlatform[] }) =>
      rerenderAnimationVideo(input.videoId, input.platforms),
    onSuccess: (_d, input) =>
      qc.invalidateQueries({ queryKey: animationVideoKeys.status(input.videoId) }),
  });
}

/**
 * Erreur d'un rendu ou d'une publication, en une phrase ; le texte brut
 * (sortie de FFmpeg, réponse d'une plateforme) reste disponible en détail.
 */
export function summarizeVideoError(raw: string | null | undefined): {
  summary: string;
  detail: string | null;
} {
  const text = (raw ?? '').trim();
  if (!text) return { summary: 'Le rendu a échoué.', detail: null };
  const first = text.split('\n')[0]!.trim();
  const short = (t: string) => (t.length > 180 ? `${t.slice(0, 179)}…` : t);
  let summary: string;
  if (/^ffmpeg/i.test(text)) summary = 'Le montage de la vidéo (FFmpeg) a échoué.';
  else if (/too_many_restarts/.test(text))
    summary = 'Le rendu s’est arrêté plusieurs fois (worker interrompu).';
  else if (/^capture/i.test(text)) summary = `Le rendu des images a échoué : ${short(first)}`;
  else if (/^téléchargement/i.test(text))
    summary = 'Une voix ou un son n’a pas pu être téléchargé.';
  else summary = short(first);
  return { summary, detail: text !== summary ? text : null };
}

/** Aperçu validé : titre, description et légende enregistrés, puis publication. */
/** Textes relus (titre, description, légende) d'une vidéo, réduits aux limites des réseaux. PUR. */
export function videoTextsPatch(t: { title: string; description: string; caption: string }) {
  return {
    yt_title: t.title.trim().slice(0, 100),
    yt_description: t.description.trim().slice(0, 5000),
    social_caption: t.caption.trim().slice(0, 2200) || null,
  };
}

/**
 * Enregistre les textes relus SANS publier (à la sortie de chaque champ) :
 * une légende modifiée n'est plus perdue si on ne clique pas « Publier ».
 */
export function useSaveAnimationVideoTexts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      videoId: string;
      animationId?: string;
      title: string;
      description: string;
      caption: string;
    }) => {
      const { error } = await db
        .from('youtube_videos')
        .update(videoTextsPatch(input))
        .eq('id', input.videoId);
      if (error) throw new AnimationVideoError('db', error.message);
    },
    onSuccess: (_d, input) => {
      if (input.animationId)
        void qc.invalidateQueries({ queryKey: animationVideoKeys.latest(input.animationId) });
    },
  });
}

export function usePublishAnimationVideo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      videoId: string;
      platforms: readonly AnimationVideoPlatform[];
      title: string;
      description: string;
      caption: string;
    }) => {
      const { error } = await db
        .from('youtube_videos')
        .update(videoTextsPatch(input))
        .eq('id', input.videoId);
      if (error) throw new AnimationVideoError('db', error.message);
      await publishAnimationVideo(input.videoId, input.platforms);
    },
    onSuccess: (_d, input) =>
      qc.invalidateQueries({ queryKey: animationVideoKeys.status(input.videoId) }),
  });
}

// ------------------------------------------------------------- lecture

export const latestVideoSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
  animation_version: z.number().int().nullable(),
  created_at: z.string(),
  yt_title: z.string().nullable(),
  yt_description: z.string().nullable(),
  social_caption: z.string().nullable(),
  synthetic: syntheticSchema.nullable().catch(null),
  duration_ms: z.number().nullable().catch(null),
  soundtrack: z.unknown().optional(),
});
export type LatestAnimationVideo = z.infer<typeof latestVideoSchema>;

/** Dernière vidéo de l'animation (la suite de l'export, en revenant sur la page). */
export function useLatestAnimationVideo(animationId: string) {
  return useQuery({
    queryKey: animationVideoKeys.latest(animationId),
    enabled: !!animationId,
    queryFn: async (): Promise<LatestAnimationVideo | null> => {
      const { data, error } = await db
        .from('youtube_videos')
        .select(
          'id, status, animation_version, created_at, yt_title, yt_description, social_caption, ' +
            'synthetic:animation_payload->synthetic, duration_ms:animation_payload->durationMs, ' +
            'soundtrack:animation_payload->soundtrack',
        )
        .eq('animation_id', animationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new AnimationVideoError('db', error.message);
      if (!data) return null;
      const parsed = latestVideoSchema.safeParse(data);
      return parsed.success ? parsed.data : null;
    },
  });
}

export const videoStatusSchema = z.object({
  status: z.string(),
  job: z
    .object({
      status: z.string(),
      current_step: z.string().nullable(),
      error: z.string().nullable(),
      created_at: z.string(),
    })
    .nullable(),
  publications: z.array(
    z.object({
      platform: z.string(),
      status: z.string(),
      external_id: z.string().nullable().optional(),
      external_url: z.string().nullable(),
      error: z.string().nullable(),
    }),
  ),
});

/**
 * TikTok sans app auditée : la vidéo n'est PAS publiée, elle est envoyée dans
 * les brouillons de l'app TikTok (mode « inbox », `publish_id` en `v_inbox…`) ;
 * il reste à la publier depuis l'app TikTok. PUR.
 */
export function isTikTokInboxDraft(p: { platform: string; external_id?: string | null }): boolean {
  return p.platform === 'tiktok' && !!p.external_id && p.external_id.startsWith('v_inbox');
}
export type AnimationVideoStatus = z.infer<typeof videoStatusSchema>;

/** État du rendu et des publications (léger : relu toutes les 5 s pendant le rendu). */
export function useAnimationVideoStatus(videoId: string | null) {
  return useQuery({
    queryKey: animationVideoKeys.status(videoId ?? ''),
    enabled: !!videoId,
    refetchInterval: (q) => {
      const d = q.state.data as AnimationVideoStatus | undefined;
      const busy =
        !d ||
        ['queued', 'processing'].includes(d.job?.status ?? '') ||
        d.publications.some((p) => p.status === 'queued' || p.status === 'uploading');
      return busy ? 5000 : false;
    },
    queryFn: async (): Promise<AnimationVideoStatus> => {
      const [video, jobs, pubs] = await Promise.all([
        db.from('youtube_videos').select('status').eq('id', videoId!).maybeSingle(),
        db
          .from('youtube_jobs')
          .select('status, current_step, error, created_at')
          .eq('video_id', videoId!)
          .order('created_at', { ascending: false })
          .limit(1),
        db
          .from('social_publications')
          .select('platform, status, external_id, external_url, error')
          .eq('video_id', videoId!),
      ]);
      if (video.error) throw new AnimationVideoError('db', video.error.message);
      return videoStatusSchema.parse({
        status: (video.data as { status?: string } | null)?.status ?? 'draft',
        job: (jobs.data as unknown[] | null)?.[0] ?? null,
        publications: pubs.data ?? [],
      });
    },
  });
}

/** Durée du lien de la vidéo rendue (s) : 7 jours. */
export const VIDEO_LINK_TTL_S = 7 * 24 * 3600;

/**
 * Lien signé de la vidéo rendue (`youtube-renders/<vidéo>/master.mp4`), ou
 * `null` si elle n'existe pas (pas encore rendue, ou supprimée après la
 * publication sur toutes les plateformes). `renderKey` : le dernier job (relu
 * quand un rendu se termine) ; `null` pendant un rendu.
 */
export function useAnimationVideoPreview(videoId: string | null, renderKey: string | null) {
  return useQuery({
    queryKey: ['animations', 'video', 'preview', videoId, renderKey],
    enabled: !!videoId && renderKey !== null,
    staleTime: 24 * 3600 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.storage
        .from('youtube-renders')
        .createSignedUrl(`${videoId}/master.mp4`, VIDEO_LINK_TTL_S);
      return data?.signedUrl ?? null;
    },
  });
}

/**
 * Échec de publication sur UNE plateforme, en une phrase qui dit quoi faire.
 * `invalid_grant` : Google (ou TikTok) a refusé l'accès enregistré — jeton
 * expiré ou révoqué : il faut relier le compte à nouveau.
 */
export function publicationErrorMessage(platform: string, raw: string | null | undefined): string {
  const label = VIDEO_PLATFORMS.find((p) => p.id === platform)?.label ?? platform;
  const text = (raw ?? '').trim();
  if (/invalid_grant|token.*(expired|revoked)|unauthori[sz]ed|401/i.test(text)) {
    const yt = platform === 'youtube';
    const code = /invalid_grant/.test(text) ? 'invalid_grant' : 'accès refusé';
    return `${label} : l’accès ${yt ? 'à la chaîne YouTube' : `au compte ${label}`} a expiré ou a été retiré (${code}). ${
      yt ? 'Relie-la' : 'Relie-le'
    } de nouveau (Paramètres de connexion), puis clique sur « Publier ».`;
  }
  if (/quota/i.test(text)) return `${label} : quota dépassé, réessaie demain.`;
  if (!text) return `${label} : la publication a échoué.`;
  const first = text.split('\n')[0]!.trim();
  return `${label} : ${first.length > 160 ? `${first.slice(0, 159)}…` : first}`;
}

/** « 1 min 12 s » */
export function videoDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m} min ${String(s % 60).padStart(2, '0')} s` : `${s} s`;
}
